import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { useTranslation } from '@wix/fe-essentials/react';
import { useCollapseItems } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { rescanTriggerGroupIdAtom, verticalsDrillInPageIdAtom } from '../../atoms/session-atoms';
import { useShowFailedAnalyzePagesModal } from '../../components/modals/show-failed-analyze-pages-modal';
import { showScanAllSiteVerificationModal } from '../../components/modals/show-scan-all-site-verification-modal';
import { SITE_LEVEL_PAGE_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useScanTimeoutManager } from '../../hooks/scan-timeout';
import { useA11yWizardAdapter } from '../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { useAnalyzeFullSite, useAnalyzeItemType, useAnalyzePage, useAnalyzeSiteLevel } from '../../queries/analyze';
import { isStaticPage } from './helpers';
var VerifyScope = /*#__PURE__*/function (VerifyScope) {
  VerifyScope[VerifyScope["AllSite"] = 0] = "AllSite";
  VerifyScope[VerifyScope["SiteLevel"] = 1] = "SiteLevel";
  VerifyScope[VerifyScope["ItemType"] = 2] = "ItemType";
  VerifyScope[VerifyScope["Page"] = 3] = "Page";
  return VerifyScope;
}(VerifyScope || {});
export const useVerify = _ref => {
  let {
    disableAxe
  } = _ref;
  const {
    a11yWizardAdapter,
    editorHost
  } = useA11yWizardAdapter();
  const {
    analyzeSiteLevel
  } = useAnalyzeSiteLevel();
  const {
    analyzePage
  } = useAnalyzePage(disableAxe);
  const {
    analyzeItemType
  } = useAnalyzeItemType();
  const {
    analyzeFullSite
  } = useAnalyzeFullSite();
  const {
    getPagesData,
    setGroupData,
    clearModifiedCompIds
  } = usePanelStateAtom();
  const {
    setScanTimeoutForItemType,
    setPageScanTimeout
  } = useScanTimeoutManager();
  const pageIdToPageDataMap = getPagesData();
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    collapseGroup
  } = useCollapseItems();
  const setVerticalsDrillInPageId = useSetAtom(verticalsDrillInPageIdAtom);
  const showFailedAnalyzePagesModal = useShowFailedAnalyzePagesModal();
  const setRescanTriggerGroupId = useSetAtom(rescanTriggerGroupIdAtom);
  const resolveVerifyScope = useCallback(async (groupData, modifiedCompIds) => {
    const {
      itemType,
      pageId,
      isTpaPage
    } = groupData;
    if (pageId === SITE_LEVEL_PAGE_ID) {
      return VerifyScope.SiteLevel;
    }
    const isGlobalSectionCompModified = modifiedCompIds.length > 0;
    let shouldVerifyAllSite = false;
    if (isGlobalSectionCompModified) {
      await showScanAllSiteVerificationModal({
        a11yWizardAdapter,
        biLogger,
        editorHost,
        handleApply: () => {
          shouldVerifyAllSite = true;
          setVerticalsDrillInPageId(undefined);
          setRescanTriggerGroupId(undefined);
        },
        handleDiscard: () => {
          shouldVerifyAllSite = false;
        },
        t
      });
    }
    if (shouldVerifyAllSite) {
      return VerifyScope.AllSite;
    }
    if (!isStaticPage(itemType)) {
      return isTpaPage ? VerifyScope.ItemType : VerifyScope.Page;
    }
    return VerifyScope.Page;
  }, [a11yWizardAdapter, biLogger, editorHost, t, setVerticalsDrillInPageId, setRescanTriggerGroupId]);
  const verifyPrivate = useCallback(async (groupData, verifyScope) => {
    const {
      itemType,
      pageId
    } = groupData;
    try {
      if (verifyScope === VerifyScope.AllSite) {
        await analyzeFullSite();
        collapseGroup();
        setScanTimeoutForItemType(ITEM_TYPES.STATIC_PAGE_V2);
        setPageScanTimeout(SITE_LEVEL_PAGE_ID);
        clearModifiedCompIds();
      }
      if (verifyScope === VerifyScope.SiteLevel) {
        await analyzeSiteLevel();
        setPageScanTimeout(pageId);
      }
      if (verifyScope === VerifyScope.ItemType) {
        await analyzeItemType(itemType);
      }
      if (verifyScope === VerifyScope.Page) {
        var _pageIdToPageDataMap$, _pageIdToPageDataMap$2;
        const urlPath = isStaticPage(itemType) ? (_pageIdToPageDataMap$ = pageIdToPageDataMap.get(pageId)) == null ? void 0 : _pageIdToPageDataMap$.urlPath : (_pageIdToPageDataMap$2 = pageIdToPageDataMap.values().find(pageData => pageData.itemType === itemType)) == null || (_pageIdToPageDataMap$2 = _pageIdToPageDataMap$2.innerRoutes) == null || (_pageIdToPageDataMap$2 = _pageIdToPageDataMap$2.find(route => route.id === pageId)) == null ? void 0 : _pageIdToPageDataMap$2.path;
        assertNotOptional(urlPath, 'urlPath');
        await analyzePage(itemType, pageId, urlPath);
        setPageScanTimeout(pageId);
      }
      assertNotOptional(verifyScope);
      setGroupData(pageId, {
        rulesStateMap: new Map()
      });
      return;
    } catch (error) {
      collapseGroup();
      await showFailedAnalyzePagesModal();
    }
  }, [setGroupData, clearModifiedCompIds, setScanTimeoutForItemType, analyzeItemType, analyzeFullSite, analyzeSiteLevel, setPageScanTimeout, pageIdToPageDataMap, analyzePage, collapseGroup, showFailedAnalyzePagesModal]);
  const verify = useCallback(async function (groupData, modifiedCompIds) {
    if (modifiedCompIds === void 0) {
      modifiedCompIds = [];
    }
    const verifyScope = await resolveVerifyScope(groupData, modifiedCompIds);
    await verifyPrivate(groupData, verifyScope);
  }, [verifyPrivate, resolveVerifyScope]);
  return {
    verify
  };
};
//# sourceMappingURL=use-verify.js.map